import {useSyncExternalStore} from 'react';

import type {WindowSize} from './types';

/*
 * Unmeasured, not zero. `width`/`height` stay undefined until a DOM has been
 * read, so the server render and the hydration render agree, and consumers can
 * still tell "not measured yet" from "measured as 0" — several of them guard on
 * exactly that (`!!width && width <= BREAKPOINT`).
 */
const UNMEASURED: WindowSize = {width: undefined, height: undefined};

/*
 * The viewport is global, so one module-scope snapshot serves every caller and
 * they all share its identity. The cache is also what makes
 * `useSyncExternalStore` legal here: it re-reads `getSnapshot` on every render
 * and loops forever if handed a fresh object each time.
 */
let snapshot: WindowSize = UNMEASURED;

const readWindowSize = (): WindowSize => {
  const {innerWidth, innerHeight} = window;

  if (snapshot.width !== innerWidth || snapshot.height !== innerHeight) {
    snapshot = {width: innerWidth, height: innerHeight};
  }

  return snapshot;
};

const getServerSnapshot = (): WindowSize => UNMEASURED;

const subscribe = (onResize: () => void): (() => void) => {
  window.addEventListener('resize', onResize);

  return () => window.removeEventListener('resize', onResize);
};

/**
 * useWindowSize
 *
 * Subscribes to viewport resizes and returns the current dimensions.
 *
 * Built on `useSyncExternalStore` rather than useState-plus-effect on purpose:
 * the store form reads the live size during the first client render instead of
 * committing a mount-time state update, and the shared snapshot keeps the
 * returned object's identity across resizes that do not change the dimensions —
 * so consumers can safely put it in a dependency array.
 *
 * @returns The current viewport width and height; both undefined on the server
 */
const useWindowSize = (): WindowSize =>
  useSyncExternalStore(subscribe, readWindowSize, getServerSnapshot);

export default useWindowSize;
